import { ABaseModel } from '../abstracts/ABaseModel.js';
import {
  IOrderItemOptionValue,
  IOrderItemOption,
  IOrderItem,
  IOrderAddress,
  IOrderStatus,
  IOrder,
  IOrderAction,
} from '../interfaces/Order.js';
import { ImageHierarchical } from './Image.js';

/**
 * OrderItemOptionValue
 */
class OrderItemOptionValue extends ABaseModel implements IOrderItemOptionValue {
  id: number;
  optionId: number;
  orderItemId: number;
  title: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * OrderItemOption
 */
class OrderItemOption extends ABaseModel implements IOrderItemOption {
  id: number;
  orderItemId: number;
  type: string;
  group: string;
  title: string;
  isFrame: boolean;
  isInsurance: boolean;
  singleValue: boolean;
  values: Array<OrderItemOptionValue>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * OrderItem
 */
class OrderItem extends ABaseModel implements IOrderItem {
  id: number;
  freeShipping: boolean;
  noDiscount: boolean;
  name: string;
  rental: boolean;
  rentalPeriod: string;
  rentalFrequency: number;
  virtual: boolean;
  price: number;
  total: number;
  currency: string;
  qty: number;
  hasItemOptions: boolean;
  hasFrameOptions: boolean;
  hasInsuranceOptions: boolean;
  shipped: boolean;
  framed: boolean;
  signed: boolean;
  editionSize: number;
  offerId: number;
  offerAmount: number;
  readyToHang: boolean;
  width: number;
  height: number;
  depth: number;
  units: string;
  incursCustomsDuties: boolean;
  shipsBy: string;
  shipsIn: number;
  shipsFromCode: string;
  options: Array<OrderItemOption>;
  productId: number;
  productType: string;
  productStatus: number;
  productPublished: string;
  productIsEdition: boolean;
  productIsOriginal: boolean;
  artId: number;
  artStatus: number;
  artTitle: string;
  artSlug: string;
  artYear: number;
  artDescription: string;
  artMaterials: string;
  artMedium: string;
  artSubMedium: string;
  artStyle: string;
  artSubject: string;
  artSubSubject: string;
  artOrientation: string;
  artImages: Array<ImageHierarchical>;
  artistId: number;
  artistAlias: string;
  artistName: string;
  skuId: number;
  skuType: string;
  skuSku: string;
  skuStock: number;
  sellerId: number;
  sellerIntegrationId: number;
  sellerName: string;
  sellerType: number;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * OrderAddress
 */
class OrderAddress extends ABaseModel implements IOrderAddress {
  id: number;
  addressType: string;
  email: string;
  telephone: string;
  firstname: string;
  lastname: string;
  company: string;
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  county: string;
  countyCode: string;
  postcode: string;
  countryCode: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * OrderStatus
 */
class OrderStatus extends ABaseModel implements IOrderStatus {
  created: string;
  status: string;
  comment: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * Order
 */
class Order extends ABaseModel implements IOrder {
  id: string;
  orderDate: string;
  state: string;
  status: string;
  storeCode: string;
  currency: string;
  email: string;
  firstname: string;
  lastname: string;
  customerId: number;
  customerNote: string;
  couponCode: string;
  rental: boolean;
  virtual: boolean;
  orderItems: Array<OrderItem>;
  billingAddress: OrderAddress;
  shippingAddress: OrderAddress;
  subtotalAmount: number;
  shippingAmount: number;
  discountAmount: number;
  taxAmount: number;
  totalAmount: number;
  creditsAmount: number;
  paymentMethod: string;
  paymentType: string;
  paymentCategory: string;
  paymentAccountEmail: string;
  paymentAccountOwner: string;
  paymentAccountBank: string;
  paymentAccountLast4: string;
  paymentCardType: string;
  paymentCardLastDigits: string;
  paymentCardOwner: string;
  paymentCardEndMonth: number;
  paymentCardEndYear: number;
  paymentCardStartMonth: number;
  paymentCardStartYear: number;
  paymentCardIssue: number;
  savedPaymentId: string;
  savedPaymentCardType: string;
  savedPaymentCardName: string;
  savedPaymentCardLast4: string;
  savedPaymentCardEndMonth: number;
  savedPaymentCardEndYear: number;
  statusHistory: Array<OrderStatus>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * OrderAction
 */
class OrderAction extends ABaseModel implements IOrderAction {
  id: string;
  action: string;
  orderId: string;
  executed: string;
  status: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

export { OrderItemOptionValue, OrderItemOption, OrderItem, OrderAddress, OrderStatus, Order, OrderAction };
